import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { SentryErrorBoundary } from './lib/sentry';
// Auth/Company/Role providers are intentionally mounted ONLY inside ProtectedRoute
// so public routes (/, /login, /signup) never initialize or query Firestore.

import ProtectedRoute from './components/ProtectedRoute';
import RoleGuard from './components/RoleGuard';
// NOTE: Do not import firebase on public routes. Auth is handled inside ProtectedRoute.

// Marketing website (public) — mounted inside THIS BrowserRouter (no second router).
import MarketingLayout from './marketing/MarketingLayout';
import MktHomePage from './marketing/pages/HomePage';
import MktFeaturesPage from './marketing/pages/FeaturesPage';
import MktPricingPage from './marketing/pages/PricingPage';
import MktHowItWorksPage from './marketing/pages/HowItWorksPage';
import MktAboutPage from './marketing/pages/AboutPage';
import MktFAQPage from './marketing/pages/FAQPage';
import MktSMSProgramPage from './marketing/pages/SMSProgramPage';
import MktForCleaningPage from './marketing/pages/ForCleaningPage';
import MktForLawnCarePage from './marketing/pages/ForLawnCarePage';
import MktForPestControlPage from './marketing/pages/ForPestControlPage';
import MktCustomersPage from './marketing/pages/CustomersPage';
import MktComingSoonPage from './marketing/pages/ComingSoonPage';
import MktContactPage from './marketing/pages/ContactPage';
import MktROICalculatorPage from './marketing/pages/ROICalculatorPage';
import MktNotFoundPage from './marketing/pages/NotFoundPage';

import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/Auth/AcceptInvite';
import Dashboard from './pages/Dashboard';
import Pipeline from './pages/Pipeline';
import Leads from './pages/Leads';
import Settings from './pages/Settings';
import CompanyProfile from './pages/Settings/CompanyProfile';
import MyAccount from './pages/Settings/MyAccount';
import TeamMembers from './pages/Settings/TeamMembers';
import RolesPermissions from './pages/Settings/RolesPermissions';
import PricingSettings from './pages/Settings/PricingSettings';
import Pipelines from './pages/Settings/Pipelines';
import Automations from './pages/Settings/Automations';
import ServiceTemplates from './pages/Settings/ServiceTemplates';
import VisitMessaging from './pages/Settings/VisitMessaging';
import CommunicationSettings from './pages/Settings/CommunicationSettings';
import ServicePrograms from './pages/Settings/ServicePrograms';
import CatalogItems from './pages/Settings/CatalogItems';
import SchedulingDefaults from './pages/Settings/SchedulingDefaults';
import AppConfig from './pages/Settings/AppConfig';
import Integrations from './pages/Settings/Integrations';
import BusinessNumber from './pages/Settings/BusinessNumber';
import DispatchSettings from './pages/Settings/DispatchSettings';
import Tasks from './pages/Tasks';
import Jobs from './pages/Jobs';
import Invoices from './pages/Invoices';
import Inbox from './pages/Inbox';
import Quotes from './pages/Quotes';
import QuoteWorkspace from './pages/QuoteWorkspace';
import QuoteDetail from './pages/QuoteDetail';
import RoofEstimator from './components/RoofEstimator';
import ClientsPage from './routes/scaffolded/ClientsPage';
import ClientDetailPage from './routes/scaffolded/ClientDetailPage';
import Calendar from './pages/Calendar';
import ForecastPage from './pages/ForecastPage';
import DispatchPage from './pages/Dispatch';
import BusinessCoach from './pages/BusinessCoach';
import Engage from './pages/Engage';
import EngageAudiences from './pages/EngageAudiences';
import EngageSMS       from './pages/EngageSMS';
import EngageEmail     from './pages/EngageEmail';
import EngageHistory   from './pages/EngageHistory';
import Playbooks from './pages/Playbooks';
import Reports from './pages/Reports';
import ReportsRevenue from './pages/ReportsRevenue';
import ReportsWinRate from './pages/ReportsWinRate';
import ReportsLeadSources from './pages/ReportsLeadSources';
import ReportsTechnician from './pages/ReportsTechnician';
import ReportsJobTypes from './pages/ReportsJobTypes';
import AdminDashboard from './pages/AdminDashboard';
import TodayMobile from './pages/TodayMobile';
import MoreMobile from './pages/MoreMobile';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Onboarding from './pages/Onboarding';
import PublicQuote from './pages/PublicQuote';
import CardSaved from './pages/CardSaved';
import WebRTCDev from './pages/Dev/WebRTCDev';
import ActiveCallBar from './components/ActiveCallBar';
import IncomingCallModal from './components/IncomingCallModal';
import WebRTCAutoRegister from './components/WebRTCAutoRegister';

// SaaS Billing (Sprint B) — strictly separate from tenant→customer Stripe/Connect.
// Reads Firestore subscription mirror; never enforces entitlement (Sprint C).
import Pricing from './pages/Pricing';
import BillingSuccess from './pages/BillingSuccess';
import BillingCancel from './pages/BillingCancel';
import BillingSettings from './pages/BillingSettings';

const WEBRTC_DEV_ENABLED = process.env.REACT_APP_WEBRTC_DEV === 'true';
// Outbound WebRTC calling essentials (in-call bar + remote-hangup teardown
// listener + SDK warm registration). Required for the real calling
// experience — decoupled from the DEV proof-page flag. Defaults ON; set
// REACT_APP_WEBRTC_OUTBOUND_ENABLED=false only to hard-disable browser calling.
const WEBRTC_OUTBOUND_ENABLED = process.env.REACT_APP_WEBRTC_OUTBOUND_ENABLED !== 'false';
const WEBRTC_INBOUND_ENABLED = process.env.REACT_APP_WEBRTC_INBOUND_ENABLED === 'true';

// Fallback UI for the top-level Sentry ErrorBoundary. Declared outside
// the App component to avoid React's "do not define components during
// render" lint — keeps reconciliation stable.
function AppErrorFallback({ error, resetError }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
        <p className="text-sm text-gray-500">
          The error has been reported. Try reloading — if it keeps happening, contact support.
        </p>
        <button
          type="button"
          onClick={() => { resetError(); window.location.reload(); }}
          className="px-4 py-2 rounded-lg text-white text-sm font-medium"
          style={{ backgroundColor: 'var(--cb-orange)' }}
          data-testid="sentry-fallback-reload-btn"
        >
          Reload
        </button>
        {process.env.NODE_ENV !== 'production' && error && (
          <pre className="text-[10px] text-left text-red-600 bg-red-50 rounded p-2 overflow-auto mt-4">
            {String(error?.message || error)}
          </pre>
        )}
      </div>
    </div>
  );
}

export default function App() {

  return (
    <SentryErrorBoundary fallback={AppErrorFallback}>
      <BrowserRouter>
      <div className="min-h-screen bg-background">
        {WEBRTC_OUTBOUND_ENABLED && <ActiveCallBar />}
        {WEBRTC_OUTBOUND_ENABLED && <WebRTCAutoRegister />}
        {WEBRTC_INBOUND_ENABLED && <IncomingCallModal />}
        <Routes>
            {/* Public Quote Approval Route — no auth required */}
            <Route path="/q/:token" element={<PublicQuote />} />

            {/* Public Card-Saved confirmation — shown to clients after Stripe card setup */}
            <Route path="/card-saved" element={<CardSaved />} />

            {/* Public Authentication Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/invite" element={<AcceptInvite />} />

            {/* Onboarding Wizard — bare shell, no AppLayout / providers */}
            <Route element={<ProtectedRoute bare />}>
              <Route path="/onboarding" element={<Onboarding />} />
              {WEBRTC_DEV_ENABLED && (
                <Route path="/dev/webrtc" element={<WebRTCDev />} />
              )}
            </Route>

            {/*
              M1 (Sprint 286): SHARED AUTHENTICATED SHELL.
              A single parent Route mounts ProtectedRoute (AuthProvider,
              CompanyProvider, RoleProvider, AppReadyGate, all Firestore-
              heavy providers, and AppLayout). React Router keeps this
              parent matched across every child navigation — so ordinary
              tab-to-tab clicks no longer remount the provider tree.
            */}
            <Route element={<ProtectedRoute />}>
              {/* Protected Dashboard Route */}
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Protected Calculator Route - SALES & ADMIN ONLY */}
              <Route
                path="/calculator"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales']}>
                    <RoofEstimator />
                  </RoleGuard>
                }
              />

              {/* Protected Pipeline Route - SALES & ADMIN ONLY */}
              <Route
                path="/pipeline"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales']}>
                    <Pipeline />
                  </RoleGuard>
                }
              />

              {/* Protected Leads Route - SALES, ADMIN & MARKETING (read-only for marketing) */}
              <Route
                path="/leads"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'marketing']}>
                    <Leads />
                  </RoleGuard>
                }
              />

              {/* Protected Lead Detail Route */}
              <Route
                path="/leads/:id"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'marketing']}>
                    <ClientDetailPage entryContext="leads" />
                  </RoleGuard>
                }
              />

              {/* Protected Clients Route */}
              <Route
                path="/clients"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'technician']}>
                    <ClientsPage />
                  </RoleGuard>
                }
              />
              <Route
                path="/clients/:id"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'technician']}>
                    <ClientDetailPage />
                  </RoleGuard>
                }
              />

              {/* Protected Tasks Route */}
              <Route
                path="/tasks"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'technician', 'marketing']}>
                    <Tasks />
                  </RoleGuard>
                }
              />

              {/* Protected Jobs Route */}
              <Route
                path="/jobs"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'technician', 'marketing']}>
                    <Jobs />
                  </RoleGuard>
                }
              />

              {/* Protected Invoices Route */}
              <Route
                path="/invoices"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales']}>
                    <Invoices />
                  </RoleGuard>
                }
              />

              {/* My Account */}
              <Route
                path="/account"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'technician', 'marketing']}>
                    <MyAccount />
                  </RoleGuard>
                }
              />

              {/* Mobile Today */}
              <Route
                path="/today"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'technician', 'marketing']}>
                    <TodayMobile />
                  </RoleGuard>
                }
              />

              {/* Mobile More Menu */}
              <Route
                path="/more"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'technician', 'marketing']}>
                    <MoreMobile />
                  </RoleGuard>
                }
              />

              {/* Calendar */}
              <Route
                path="/calendar"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'technician', 'marketing']}>
                    <Calendar />
                  </RoleGuard>
                }
              />

              {/* Forecast */}
              <Route
                path="/forecast"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales']}>
                    <ForecastPage />
                  </RoleGuard>
                }
              />

              {/* Dispatch */}
              <Route
                path="/dispatch"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'technician']}>
                    <DispatchPage />
                  </RoleGuard>
                }
              />

              {/* Engage — nested tabs */}
              <Route
                path="/engage"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales', 'marketing']}>
                    <Engage />
                  </RoleGuard>
                }
              >
                <Route path="audiences" element={<EngageAudiences />} />
                <Route path="sms"       element={<EngageSMS />}       />
                <Route path="email"     element={<EngageEmail />}     />
                <Route path="history"   element={<EngageHistory />}   />
              </Route>

              {/* Business Coach */}
              <Route
                path="/coach"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin']}>
                    <BusinessCoach />
                  </RoleGuard>
                }
              />

              {/* Inbox */}
              <Route
                path="/inbox"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales']}>
                    <Inbox />
                  </RoleGuard>
                }
              />

              {/* Playbooks */}
              <Route
                path="/playbooks"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales']}>
                    <Playbooks />
                  </RoleGuard>
                }
              />

              {/* Quotes */}
              <Route
                path="/quotes"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales']}>
                    <Quotes />
                  </RoleGuard>
                }
              />
              <Route
                path="/quotes/new"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales']}>
                    <QuoteWorkspace />
                  </RoleGuard>
                }
              />
              <Route
                path="/quotes/:quoteId"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales']}>
                    <QuoteDetail />
                  </RoleGuard>
                }
              />
              <Route
                path="/quotes/:leadId/:quoteId"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin', 'sales']}>
                    <QuoteDetail />
                  </RoleGuard>
                }
              />

              {/* Settings — nested */}
              <Route
                path="/settings"
                element={
                  <RoleGuard allowedRoles={['owner', 'admin']}>
                    <Settings />
                  </RoleGuard>
                }
              >
                <Route index element={<Navigate to="/settings/company" replace />} />
                <Route path="company" element={<CompanyProfile />} />
                <Route path="account" element={<MyAccount />} />
                <Route path="team" element={<TeamMembers />} />
                <Route path="roles" element={<RolesPermissions />} />
                <Route path="pricing" element={<PricingSettings />} />
                <Route path="pipelines" element={<Pipelines />} />
                <Route path="automations" element={<Automations />} />
                <Route path="service-templates" element={<ServiceTemplates />} />
                <Route path="service-programs" element={<ServicePrograms />} />
                <Route path="catalog-items" element={<CatalogItems />} />
                <Route path="scheduling-defaults" element={<SchedulingDefaults />} />
                <Route path="visit-messaging" element={<VisitMessaging />} />
                <Route path="communication" element={<CommunicationSettings />} />
                <Route path="business-number" element={<BusinessNumber />} />
                <Route path="dispatch" element={<DispatchSettings />} />
                <Route path="config" element={<AppConfig />} />
                <Route path="integrations" element={<Integrations />} />
              </Route>

              {/* Reports */}
              <Route path="/reports" element={<RoleGuard allowedRoles={['owner', 'admin', 'sales']}><Reports /></RoleGuard>} />
              <Route path="/reports/revenue" element={<RoleGuard allowedRoles={['owner', 'admin', 'sales']}><ReportsRevenue /></RoleGuard>} />
              <Route path="/reports/win-rate" element={<RoleGuard allowedRoles={['owner', 'admin', 'sales']}><ReportsWinRate /></RoleGuard>} />
              <Route path="/reports/lead-sources" element={<RoleGuard allowedRoles={['owner', 'admin', 'sales']}><ReportsLeadSources /></RoleGuard>} />
              <Route path="/reports/technician" element={<RoleGuard allowedRoles={['owner', 'admin']}><ReportsTechnician /></RoleGuard>} />
              <Route path="/reports/job-types" element={<RoleGuard allowedRoles={['owner', 'admin', 'sales']}><ReportsJobTypes /></RoleGuard>} />

              {/* SaaS Billing (Sprint B) — strictly separate from tenant→customer Stripe.
                  These reveal the subscription state; they DO NOT gate any other route. */}
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/billing" element={<RoleGuard allowedRoles={['owner']}><BillingSettings /></RoleGuard>} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/billing/cancel" element={<BillingCancel />} />

              {/* Platform Admin */}
              <Route path="/admin" element={<AdminDashboard />} />
            </Route>

            {/* Root & marketing website — public, mounted in the shared router.
                Marketing pricing lives at /plans; the app's protected /pricing
                billing route is preserved below unchanged. */}
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<MktHomePage />} />
              <Route path="/features" element={<MktFeaturesPage />} />
              <Route path="/plans" element={<MktPricingPage />} />
              <Route path="/how-it-works" element={<MktHowItWorksPage />} />
              <Route path="/about" element={<MktAboutPage />} />
              <Route path="/faq" element={<MktFAQPage />} />
              <Route path="/sms-program" element={<MktSMSProgramPage />} />
              <Route path="/for-cleaning" element={<MktForCleaningPage />} />
              <Route path="/for-lawn-care" element={<MktForLawnCarePage />} />
              <Route path="/for-pest-control" element={<MktForPestControlPage />} />
              <Route path="/customers" element={<MktCustomersPage />} />
              <Route path="/blog" element={<MktComingSoonPage pageName="Blog" />} />
              <Route path="/contact" element={<MktContactPage />} />
              <Route path="/roi-calculator" element={<MktROICalculatorPage />} />
            </Route>

            {/* Catch all — branded marketing 404 (after every app + marketing route) */}
            <Route path="*" element={<MarketingLayout />}>
              <Route path="*" element={<MktNotFoundPage />} />
            </Route>
          </Routes>
          
        <Toaster position="top-right" />
      </div>
    </BrowserRouter>
    </SentryErrorBoundary>
  );
}
